import type { MapBounds, MapPoint } from './types';

const clamp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

// Leaflet's `getBounds()` returns UNWRAPPED longitudes once the map is
// zoomed out past a single world copy (e.g. -416..-220 at world view).
// Feeding those raw to a PostGIS `ST_Within` envelope excludes every real
// product. Normalise to a valid lat/lng box; when the viewport spans (near)
// the whole world or wraps out of range, widen longitude to the full
// [-180, 180] so the bbox doesn't constrain anything.
export const sanitizeMapBounds = (raw: MapBounds): MapBounds => {
  const spansWorld =
    raw.northEastLng - raw.southWestLng >= 350 ||
    raw.northEastLng > 180 ||
    raw.southWestLng < -180;
  return {
    northEastLat: clamp(raw.northEastLat, -90, 90),
    southWestLat: clamp(raw.southWestLat, -90, 90),
    northEastLng: spansWorld ? 180 : clamp(raw.northEastLng, -180, 180),
    southWestLng: spansWorld ? -180 : clamp(raw.southWestLng, -180, 180),
  };
};

// Axis-aligned bounding box of a point set, as Leaflet's
// `[[swLat, swLng], [neLat, neLng]]` LatLngBounds tuple. Returns null for
// an empty set so callers can skip `fitBounds`.
export const boundsFromPoints = (
  points: ReadonlyArray<MapPoint>
): [[number, number], [number, number]] | null => {
  if (points.length === 0) return null;
  let minLat = Number.POSITIVE_INFINITY;
  let minLng = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
};
